"use client";

import { useCurrentCityName } from "@/hooks/use-current-city";
import { getCityStats } from "@/info/seo/stats";
import { escapeHtml } from "@/utils/security";
import { formatBedrooms } from "@/utils/sup/get-meta-data";
import { usePathname } from "next/navigation";
import { memo, useMemo } from "react";

interface SeoBannerProps {
	// For project/unit pages
	projectName?: string;
	locality?: string;
	propertyTypes?: string[];
	availableUnitsCount?: number;
	availableBedrooms?: number[];
}

/**
 * Universal SEO banner component that displays dynamic statistics
 * Works for search pages, project pages, and unit pages
 * Detects route and shows appropriate content
 */
export const SEOContentForProjectOrSup = memo(function SEOContentForProjectOrSup({
	projectName,
	locality: projectLocality,
	propertyTypes,
	availableUnitsCount,
	availableBedrooms,
}: SeoBannerProps = {}) {
	const pathname = usePathname();
	const city = useCurrentCityName();

	// Detect page type from route
	const pageType = useMemo(() => {
		if (pathname?.startsWith("/projects/")) return "project";
		if (pathname?.startsWith("/units/") || pathname?.startsWith("/unit/")) return "unit";
		return "search";
	}, [pathname]);

	const stats = getCityStats(city);

	// Format city name for display (capitalize first letter)
	// escapeHtml is used here although city comes from whitelist, just for consistency
	const cityDisplay = escapeHtml(city.charAt(0).toUpperCase() + city.slice(1));

	// Determine if this is a generic search (no property type specified)
	const isGenericSearch = !propertyTypes || propertyTypes.length === 0;

	// Determine property type and labels based on property types
	const { isVillaSearch, singularLabel, pluralLabel, resalePropertyType } = useMemo(() => {
		if (isGenericSearch) {
			// For generic searches, we'll use special labels
			return {
				isVillaSearch: false,
				singularLabel: "flats and houses",
				pluralLabel: "apartments and villas",
				resalePropertyType: "properties",
			};
		}
		const isVilla =
			propertyTypes?.some(type => type.toLowerCase().includes("villa") || type.toLowerCase().includes("house")) ||
			false;
		return {
			isVillaSearch: isVilla,
			singularLabel: isVilla ? "houses" : pageType === "project" ? "flats" : "flat",
			pluralLabel: isVilla ? "villas" : "apartments",
			resalePropertyType: isVilla ? "house" : "apartment",
		};
	}, [propertyTypes, pageType, isGenericSearch]);

	// Get bedroom configuration
	const bedroomLabel = useMemo(
		() => (Array.isArray(availableBedrooms) ? formatBedrooms(availableBedrooms) : ""),
		[availableBedrooms],
	);

	// Format numbers with commas
	const formattedListings = availableUnitsCount;
	const formattedVisits = stats.yearlyVisits.toLocaleString();
	const formattedBuyers = stats.buyersInLakhs;
	const verifiedSellers = "30,000+";
	const totalBuyers = "400,000+";

	// Determine locations for different parts of the text
	const { primaryLocation, secondaryLocation, finalLocation } = useMemo(() => {
		// Escape user provided inputs
		const safeProjectName = escapeHtml(projectName || "");
		const safeLocality = escapeHtml(projectLocality || "");

		if (pageType === "project") {
			// Project page: use projectName and locality
			return {
				primaryLocation: safeProjectName || cityDisplay,
				secondaryLocation: safeLocality || cityDisplay,
				finalLocation: safeLocality || cityDisplay,
			};
		}
		// Search page: use provided locality and projectName
		return {
			primaryLocation: safeProjectName || safeLocality || cityDisplay,
			secondaryLocation:
				safeProjectName && safeLocality ? safeLocality : safeProjectName || safeLocality || cityDisplay,
			finalLocation: safeLocality || cityDisplay,
		};
	}, [pageType, projectName, projectLocality, cityDisplay]);

	// Content generators for each page type
	const contentGenerators = useMemo(
		() => ({
			unitPage: (params: {
				isVillaSearch: boolean;
				bedroomLabel: string;
				primaryLocation: string;
				secondaryLocation: string;
				finalLocation: string;
				cityDisplay: string;
			}) => {
				const propertyTypeFirst = params.isVillaSearch ? "house and villas" : "flat and apartments";
				const propertyTypeLast = params.isVillaSearch ? "houses and villas" : "flats and apartments";

				return {
					text: `Find your perfect ${params.bedroomLabel ? `${params.bedroomLabel} ` : ""}${propertyTypeFirst} in ${params.primaryLocation} with the city's leading real estate broker. With over 10 years of experience in the real estate in ${params.cityDisplay} market, we have built a trusted platform that connects buyers and sellers seamlessly through innovation, transparency, and reliability.

Join a growing community of {sellers} verified sellers and {totalBuyers} buyers who have found their dream properties in ${params.finalLocation} with us. Whether you're looking for an affordable flat or a luxury apartment, our curated listings are verified for authenticity and tailored to fit every budget and lifestyle.

What sets us apart is our zero brokerage promise, without any subscription fee, helping buyers and sellers save more while enjoying expert guidance from one of the most experienced brokers in ${params.cityDisplay}. You can visit the properties directly via our platform. Our technology-driven tools make searching, comparing, and connecting easier than ever, while our dedicated team ensures a smooth, transparent transaction every step of the way. We also provide legal verification and documentation services, completely free of cost.

With millions of visits conducted every year, our platform continues to redefine how real estate in ${params.cityDisplay} is bought and sold. Explore trusted resale ${params.bedroomLabel ? `${params.bedroomLabel} ` : ""}${propertyTypeLast} in ${params.primaryLocation} and experience the convenience, confidence, and savings that come with choosing ${params.finalLocation}'s most reliable real estate broker, with zero brokerage guaranteed.`,
					positionClass: "",
				};
			},

			projectPage: (params: {
				singularLabel: string;
				pluralLabel: string;
				bedroomLabel: string;
				resalePropertyType: string;
				primaryLocation: string;
				secondaryLocation: string;
				finalLocation: string;
				cityDisplay: string;
				isVillaSearch: boolean;
			}) => {
				const propertyTypeFirst = params.isVillaSearch ? "house and villas" : "flat and apartments";
				const propertyTypeLast = params.isVillaSearch ? "houses and villas" : "flats and apartments";

				return {
					text: `Find your perfect ${params.bedroomLabel ? `${params.bedroomLabel} ` : ""}${propertyTypeFirst} in ${params.primaryLocation} with the city's leading real estate broker. With over 10 years of experience in the real estate in ${params.cityDisplay} market, we have built a trusted platform that connects buyers and sellers seamlessly through innovation, transparency, and reliability.

Join a growing community of {sellers} verified sellers and {totalBuyers} buyers who have found their dream properties in ${params.finalLocation} with us. Whether you're looking for an affordable flat or a luxury apartment, our curated listings are verified for authenticity and tailored to fit every budget and lifestyle.

What sets us apart is our zero brokerage promise, without any subscription fee, helping buyers and sellers save more while enjoying expert guidance from one of the most experienced brokers in ${params.cityDisplay}. You can visit the properties directly via our platform. Our technology-driven tools make searching, comparing, and connecting easier than ever, while our dedicated team ensures a smooth, transparent transaction every step of the way. We also provide legal verification and documentation services, completely free of cost.

With millions of visits conducted every year, our platform continues to redefine how real estate in ${params.cityDisplay} is bought and sold. Explore trusted resale ${params.bedroomLabel ? `${params.bedroomLabel} ` : ""}${propertyTypeLast} in ${params.primaryLocation} and experience the convenience, confidence, and savings that come with choosing ${params.finalLocation}'s most reliable real estate broker, with zero brokerage guaranteed.`,
					positionClass: "",
				};
			},

			searchPage: (params: {
				singularLabel: string;
				pluralLabel: string;
				bedroomLabel: string;
				resalePropertyType: string;
				primaryLocation: string;
				secondaryLocation: string;
				finalLocation: string;
				cityDisplay: string;
				hasProject: boolean;
				isVillaSearch: boolean;
			}) => {
				// Search with project name
				if (params.hasProject) {
					return {
						text: `Find your perfect ${params.bedroomLabel ? `${params.bedroomLabel} ` : ""}${params.bedroomLabel ? "flat and apartments" : "home from {count} verified flats"} in ${params.primaryLocation} with the city's leading real estate broker. With over 10 years of experience in the real estate in ${params.cityDisplay} market, we have built a trusted platform that connects buyers and sellers seamlessly through innovation, transparency, and reliability.

Join a growing community of {sellers} verified sellers and {totalBuyers} buyers who have found their dream properties in ${params.finalLocation} with us. Whether you're looking for an affordable flat or a luxury apartment, our curated listings are verified for authenticity and tailored to fit every budget and lifestyle.

What sets us apart is our zero brokerage promise, without any subscription fee, helping buyers and sellers save more while enjoying expert guidance from one of the most experienced brokers in ${params.cityDisplay}. You can visit the properties directly via our platform. Our technology-driven tools make searching, comparing, and connecting easier than ever, while our dedicated team ensures a smooth, transparent transaction every step of the way. We also provide legal verification and documentation services, completely free of cost.

With millions of visits conducted every year, our platform continues to redefine how real estate in ${params.cityDisplay} is bought and sold. Explore trusted resale ${params.bedroomLabel ? `${params.bedroomLabel} flats and apartments` : "flats, apartments, houses, and villas"} in ${params.primaryLocation} and experience the convenience, confidence, and savings that come with choosing ${params.finalLocation}'s most reliable real estate broker, with zero brokerage guaranteed.`,
						positionClass: "",
					};
				}

				// Search with location (no project)
				if (params.primaryLocation !== params.cityDisplay) {
					const firstProperty = params.isVillaSearch ? "houses" : "flats";
					const secondProperty = params.isVillaSearch ? "villas" : "apartments";

					return {
						text: `Find your perfect ${params.bedroomLabel ? `${params.bedroomLabel} ` : ""}home from {count} verified ${firstProperty} in ${params.primaryLocation}, premium ${params.bedroomLabel ? `${params.bedroomLabel} ` : ""}${secondProperty} in ${params.primaryLocation}, and exclusive villas with the city's leading real estate broker. With over 10 years of experience in the real estate in ${params.cityDisplay} market, we have built a trusted platform that connects buyers and sellers seamlessly through innovation, transparency, and reliability.

Join a growing community of {sellers} verified sellers and {totalBuyers} buyers who have found their dream properties in ${params.cityDisplay} with us. Whether you're looking for an affordable flat or a luxury apartment, our curated listings are verified for authenticity and tailored to fit every budget and lifestyle.

What sets us apart is our zero brokerage promise, without any subscription fee, helping buyers and sellers save more while enjoying expert guidance from one of the most experienced brokers in ${params.cityDisplay}. You can visit the properties directly via our platform. Our technology-driven tools make searching, comparing, and connecting easier than ever, while our dedicated team ensures a smooth, transparent transaction every step of the way. We also provide legal verification and documentation services, completely free of cost.

With millions of visits conducted every year, our platform continues to redefine how real estate in ${params.cityDisplay} is bought and sold. Explore trusted resale ${params.bedroomLabel ? `${params.bedroomLabel} ` : ""}flats, apartments, houses, and villas in ${params.primaryLocation}, and experience the convenience, confidence, and savings that come with choosing ${params.cityDisplay}'s most reliable real estate broker, with zero brokerage guaranteed.`,
						positionClass: "",
					};
				}

				// Generic search (no location, no project)
				const firstProperty = params.isVillaSearch ? "houses" : "flats";
				const secondProperty = params.isVillaSearch ? "villas" : "apartments";

				return {
					text: `Find your perfect home from {count} verified ${firstProperty} in ${params.cityDisplay}, premium ${secondProperty} in ${params.cityDisplay}, and exclusive villas with the city's leading real estate broker. With over 10 years of experience in the real estate in ${params.cityDisplay} market, we have built a trusted platform that connects buyers and sellers seamlessly through innovation, transparency, and reliability.

Join a growing community of {sellers} verified sellers and {totalBuyers} buyers who have found their dream properties in ${params.cityDisplay} with us. Whether you're looking for an affordable flat or a luxury apartment, our curated listings are verified for authenticity and tailored to fit every budget and lifestyle.

What sets us apart is our zero brokerage promise, without any subscription fee, helping buyers and sellers save more while enjoying expert guidance from one of the most experienced brokers in ${params.cityDisplay}. You can visit the properties directly via our platform. Our technology-driven tools make searching, comparing, and connecting easier than ever, while our dedicated team ensures a smooth, transparent transaction every step of the way. We also provide legal verification and documentation services, completely free of cost.

With millions of visits conducted every year, our platform continues to redefine how real estate in ${params.cityDisplay} is bought and sold. Explore trusted resale flats, apartments, houses, and villas in ${params.cityDisplay}, and experience the convenience, confidence, and savings that come with choosing ${params.cityDisplay}'s most reliable real estate broker, with zero brokerage guaranteed.`,
					positionClass: "",
				};
			},
		}),
		[],
	);

	// Get content based on page type using content generators
	const content = useMemo(() => {
		const hasProject = !!projectName;
		const params = {
			isVillaSearch,
			singularLabel,
			pluralLabel,
			bedroomLabel,
			resalePropertyType,
			primaryLocation,
			secondaryLocation,
			finalLocation,
			cityDisplay,
			hasProject,
		};

		return contentGenerators[pageType === "unit" ? "unitPage" : pageType === "project" ? "projectPage" : "searchPage"](
			params,
		);
	}, [
		contentGenerators,
		pageType,
		isVillaSearch,
		singularLabel,
		pluralLabel,
		bedroomLabel,
		resalePropertyType,
		primaryLocation,
		secondaryLocation,
		finalLocation,
		cityDisplay,
		projectName,
	]);

	// Replace placeholders with actual values
	const finalText = content.text
		.replace("{count}", `<span class="font-semibold text-white">${formattedListings}+</span>`)
		.replace("{visits}", `<span class="font-semibold text-white">${formattedVisits}</span>`)
		.replace("{buyers}", `<span class="font-semibold text-white">${formattedBuyers}</span>`)
		.replace("{sellers}", `<span class="font-semibold text-white">${verifiedSellers}</span>`)
		.replace("{totalBuyers}", `<span class="font-semibold text-white">${totalBuyers}</span>`);

	return (
		<div className="container mx-auto px-4 py-8 md:py-12">
			<div className="text-white leading-relaxed text-center text-sm md:text-base space-y-4">
				{finalText.split("\n\n").map((paragraph, index) => (
					<p key={index} dangerouslySetInnerHTML={{ __html: paragraph }} />
				))}
			</div>
		</div>
	);
});
